import { CBCT_SectionOrientation } from '@/shared/api/protocol-ts/model/dto_asset_common_pb';
import { ToothFullHeadMPRSliceGeometrySource } from '@/shared/api/protocol-ts/model/dto_report_tooth_pb';
import { TOOL_NAME_TO_ICON_MAPPING } from '@/shared/config';
import type {
  FullHeadMPRInputMode,
  FullHeadMPRProjection,
  FullHeadMPRToothSliceOrientation,
  ToothBasisLocalAxisName,
  ToothBasisSeriesName,
  ToothGeometrySource,
  ToothParityDisplacementReference,
  ToothParityInPlaneReference,
} from '@/shared/graphics/medicalImageRender/MedicalImageRender';
import type { IconNames } from '@/shared/ui';

import { MedicalImagesGroups } from '@/entities/assets';
import { type ToothMPRSeriesKey } from '@/entities/reports';

import {
  type MPRImageTool,
  type MPRInputMode,
  type MPRTool,
} from '@/features/MPR';

export {
  DEFAULT_CARD_AXIAL_CROWN_INSET_MM,
  DEFAULT_CARD_AXIAL_ROOT_INSET_MM,
  DEFAULT_CARD_AXIAL_SHORT_CROWN_RATIO,
  DEFAULT_CARD_AXIAL_SHORT_ROOT_RATIO,
  DEFAULT_CARD_CROSS_SECTION_PAIR_SPREAD_MM,
  DEFAULT_TOOTH_SLICE_OPTIONS,
  DEFAULT_TOOTH_SLICE_OPTIONS_BY_SERIES,
  FULL_HEAD_MPR_PROJECTION_TO_SERIES,
  FULL_HEAD_MPR_SAVED_VIEW_PREVIEW_SIDE_PX,
  ORIENTATION_TO_SERIES,
  SERIES_TO_ORIENTATION,
  TOOTH_AXIAL_CORONAL_ROOT_LEVEL_RATIO,
  TOOTH_AXIAL_LEVEL_MIN_TISSUE_BINS,
  TOOTH_AXIAL_ROOT_LEVEL_RATIO,
  TOOTH_BASIS_SERIES_ORDER,
  TOOTH_CROWN_LENGTH_RATIO,
  TOOTH_QUADRANTS,
  TOOTH_ROOT_DEPTH_SCAN_RANGE,
  TOOTH_ROOT_DEPTH_WINDOW_ROWS,
  TOOTH_ROOT_ISLAND_FLOOR_RATIO,
  TOOTH_ROOT_MIN_ISLAND_BINS,
  TOOTH_ROOT_ROWS_FALLBACK_RATIO,
  TOOTH_SLICE_THICKNESS_OPTIONS_MM,
  TOOTH_TISSUE_MASS_FLOOR_RATIO,
  VALID_FDI_ISO_NUMBERS,
} from '@/shared/graphics/medicalImageRender/MedicalImageRender';

export const TOOTH_SLICE_LOOKAHEAD_PX = 200;
export const FULL_HEAD_MPR_SAVED_VIEW_ZOOM_SIDE_PX = 2048;
export const FULL_HEAD_MPR_SESSION_PROJECTIONS: FullHeadMPRProjection[] = [
  'axial',
  'coronal',
  'sagittal',
];

export const FULL_HEAD_MPR_SLICE_PLACEHOLDERS = [
  { key: 'axial-crown', aspectRatio: 1 },
  { key: 'axial-middle', aspectRatio: 1 },
  { key: 'axial-root', aspectRatio: 1 },
  { key: 'frontal-centroid', aspectRatio: 0.63 },
  { key: 'sagittal-mesial', aspectRatio: 0.46 },
  { key: 'sagittal-distal', aspectRatio: 0.46 },
];

export const FULL_HEAD_MPR_ERROR_CODES = {
  dataStream: 'data-stream',
  missingVolume: 'missing-volume',
  startViewer: 'start-viewer',
} as const;

export const FULL_HEAD_MPR_INPUT_MODE_BY_MODE: Record<
  MPRInputMode,
  FullHeadMPRInputMode
> = {
  mainMode: 'navigate',
  contrastBrightnessMode: 'brightnessContrast',
  panMode: 'pan',
  zoomMode: 'zoom',
  scrollThroughVolumeMode: 'scroll',
  rotateMode: 'rotate',
  rotateAxesMode: 'rotateAxes',
};

export const FULL_HEAD_MPR_IMAGE_TOOL_ICONS: Record<MPRImageTool, IconNames> = {
  brightnessContrast: 'brightness',
  sharpness: TOOL_NAME_TO_ICON_MAPPING.sharpness,
};

export const FULL_HEAD_MPR_TOOLS: MPRTool[] = [
  'mainMode',
  'contrastBrightnessMode',
  'panMode',
  'zoomMode',
  'scrollThroughVolumeMode',
  'rotateMode',
  'rotateAxesMode',
  'reset',
];

export const TOOTH_BASIS_SERIES_TO_GROUP: Record<
  ToothBasisSeriesName,
  MedicalImagesGroups
> = {
  axial: MedicalImagesGroups.OrientationAxial,
  sagittal: MedicalImagesGroups.OrientationVestibuloOral,
  frontal: MedicalImagesGroups.OrientationMesioDistal,
};

export const SERIES_TO_OPTIONS_KEY: Record<
  ToothBasisSeriesName,
  ToothMPRSeriesKey
> = {
  axial: 'Axial',
  sagittal: 'VestibuloOral',
  frontal: 'MesioDistal',
};

export const ORIENTATION_GROUP_TYPES: MedicalImagesGroups[] = [
  MedicalImagesGroups.OrientationAxial,
  MedicalImagesGroups.OrientationVestibuloOral,
  MedicalImagesGroups.OrientationMesioDistal,
];

export const CLIENT_SLICE_GUIDE_SOURCES: Record<
  ToothBasisSeriesName,
  ToothBasisSeriesName[]
> = {
  axial: ['sagittal', 'frontal'],
  sagittal: ['frontal', 'axial'],
  frontal: ['sagittal', 'axial'],
};

export const SERIES_OFFSET_AXIS: Record<
  ToothBasisSeriesName,
  ToothBasisLocalAxisName
> = {
  axial: 'cr',
  sagittal: 'md',
  frontal: 'vo',
};

export const GEOMETRY_SOURCE_TO_PROTO: Record<
  ToothGeometrySource | 'view',
  ToothFullHeadMPRSliceGeometrySource
> = {
  protocol:
    ToothFullHeadMPRSliceGeometrySource.ToothFullHeadMPRSliceGeometrySourceProtocol,
  approximated:
    ToothFullHeadMPRSliceGeometrySource.ToothFullHeadMPRSliceGeometrySourceApproximated,
  view: ToothFullHeadMPRSliceGeometrySource.ToothFullHeadMPRSliceGeometrySourceView,
};

export const GUIDE_AXIS_EPSILON = 1e-6;

export const PROBE_PROFILE_SAMPLES = 64;

export const MINIMUM_POSITION_STEP_MM = 1e-6;
export const STRIDE_EQUALITY_TOLERANCE_MM = 1e-6;
export const MIN_WINDOW_WIDTH_HU = 1;

export const ENGINE_ORIENTATION_BY_SECTION: Partial<
  Record<CBCT_SectionOrientation, FullHeadMPRToothSliceOrientation>
> = {
  [CBCT_SectionOrientation.SectionOrientationAxial]: 'axial',
  [CBCT_SectionOrientation.SectionOrientationVestibuloOral]: 'vestibuloOral',
  [CBCT_SectionOrientation.SectionOrientationMesioDistal]: 'mesioDistal',
};

export const PARITY_PIXEL_TRANSFORMS = [
  'identity',
  'flip-u',
  'flip-v',
  'rot180',
] as const;

export const SWEEP_REFERENCES: ToothParityDisplacementReference[] = [
  'outer-box-min',
  'outer-box-max',
  'origin',
];
export const SWEEP_SIGNS: (1 | -1)[] = [1, -1];
export const SWEEP_IN_PLANE_REFERENCES: ToothParityInPlaneReference[] = [
  'outer-box-center',
  'origin',
];
export const SWEEP_AXIS_SWAPS = [false, true];
export const SWEEP_PLANE_SPACES: ('tooth' | 'volume')[] = ['volume', 'tooth'];

export const PRIMARY_QUADRANT_OFFSET = 4;

export const MAX_TOOTH_BASIS_SERIES_ABORTS = 3;

export const TOOTH_SERIES_ABANDON_GRACE_MS = 0;

export const TOOTH_BASIS_SLICE_ZOOM_CACHE_LIMIT = 6;

export const TOOTH_BASIS_SERIES_CACHE_LIMIT = 8;

export const TOOTH_SLICE_STEP_OPTIONS_MM = [0.25, 0.5, 1, 1.5, 2, 3];
